/**
 * Environment configuration
 *
 * All environment variables must be prefixed with REACT_APP_ to be available
 * in the browser. This file provides type-safe access to environment variables.
 */

interface EnvConfig {
  // API Configuration
  apiBaseUrl: string;
  apiTimeout: number;

  // Application Configuration
  appName: string;
  appVersion: string;

  /** Lowercased emails allowed to see the Analytics ribbon (must match backend allowlist). */
  superadminEmails: readonly string[];

  // Environment
  nodeEnv: string;
  isDevelopment: boolean;
  isProduction: boolean;
  isTest: boolean;
}

const getEnvVar = (key: string, defaultValue: string = ''): string => {
  return process.env[key] ?? defaultValue;
};

const getEnvVarAsNumber = (key: string, defaultValue: number): number => {
  const value = process.env[key];
  if (value === undefined || value === '') {
    return defaultValue;
  }
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

function parseCommaSeparatedEmails(raw: string): readonly string[] {
  const list = raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((e) => e.length > 0 && e.includes('@'));
  return Object.freeze(list);
}

export const env: EnvConfig = {
  // API Configuration
  apiBaseUrl: getEnvVar('REACT_APP_API_BASE_URL', 'http://127.0.0.1:5000/api/v1'),
  apiTimeout: getEnvVarAsNumber('REACT_APP_API_TIMEOUT', 30000),

  // Application Configuration
  appName: getEnvVar('REACT_APP_NAME', 'React App'),
  appVersion: getEnvVar('REACT_APP_VERSION', '0.1.0'),

  superadminEmails: parseCommaSeparatedEmails(
    getEnvVar('REACT_APP_SUPERADMIN_EMAILS', '')
  ),

  // Environment
  nodeEnv: getEnvVar('NODE_ENV', 'development'),
  isDevelopment: process.env.NODE_ENV === 'development',
  isProduction: process.env.NODE_ENV === 'production',
  isTest: process.env.NODE_ENV === 'test',
};

// Freeze the config to prevent accidental modifications
Object.freeze(env);

/**
 * Whether the signed-in user is a superadmin (Analytics ribbon; org-wide filters).
 * Must match the same addresses as backend REPORT_DOWNLOAD_EMAIL_ALLOWLIST.
 * If empty, no one is treated as superadmin here (everyone sees only their data in the UI).
 */
export function isSuperadminEmail(email: string | null | undefined): boolean {
  const normalized = email?.trim().toLowerCase();
  if (!normalized) return false;
  if (env.superadminEmails.length === 0) return false;
  return env.superadminEmails.includes(normalized);
}

/** CRA `npm start` on loopback only — production builds never match. */
export function isLocalhostDev(): boolean {
  if (process.env.NODE_ENV !== 'development') return false;
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '::1';
}

export default env;
